import type { SortDirection } from '@rialtic/types'
import { FilterNames } from '@rialtic/types'
import type { ColumnSort, SortingState } from '@tanstack/vue-table'
import type { MaybeRef } from '@vueuse/core'
import { type ModelRef, unref } from 'vue'

export interface SortStateLocal<T> {
  column: keyof T
  sort?: SortDirection
}

export const sortBySortState =
  <T extends Record<string, unknown>>(sortState: {
    column: keyof T
    sort?: SortDirection
  }) =>
  (a: T, b: T) => {
    const aValue = a[sortState.column]

    if (aValue == null) return 0

    const bValue = b[sortState.column]

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      const result = aValue.localeCompare(bValue)

      return sortState.sort === 'desc' ? result * -1 : result
    }

    if (typeof aValue === 'number' && typeof bValue === 'number') {
      const result = bValue - aValue

      return sortState.sort === 'asc' ? result * -1 : result
    }

    return 0
  }

export const useBasicSort = <T extends Record<string, unknown>>(
  data: MaybeRef<T[]> | ModelRef<T[]>,
  sortState: MaybeRef<SortStateLocal<T>>,
) =>
  computed(() => {
    const maybeArray = unref(data) as T[]

    return maybeArray && Array.isArray(maybeArray)
      ? [...maybeArray].sort(sortBySortState<T>(unref(sortState)))
      : []
  })

export type SortColAccessorMap = Record<string, string>

export const useUrlSortState = (
  columnSortMap: MaybeRefOrGetter<SortColAccessorMap>,
) => {
  const { normalizedParams, setUrlParams, deleteUrlParam } = useUrlParamsState()

  const sortStateToURLParam = (columnSort?: ColumnSort): string | null => {
    if (!columnSort) return null

    const direction = columnSort.desc ? 'desc' : 'asc'

    const sortMap = toValue(columnSortMap)
    const key = sortMap[columnSort.id as keyof typeof sortMap] || columnSort.id

    return `${key},1,${direction}`
  }

  const urlParamToSortState = (param: string | null): ColumnSort | null => {
    try {
      if (!param) {
        throw new Error('No param')
      }

      const match = param.match(
        /(?<name>[^,]+),(?<priority>[^,]+),(?<direction>[^,]+)/,
      )

      if (!match) throw new Error('No match')

      const { groups } = match

      if (!groups) throw new Error('No groups')

      const sortMap = toValue(columnSortMap)

      const column = Object.keys(sortMap).find(
        (key) => toValue(sortMap)[key] === groups.name,
      )

      return {
        id: column ?? groups.name,
        desc: Boolean(groups.direction === 'desc'),
      }
    } catch {
      return null
    }
  }

  const urlSortState = computed<string | null>(() => {
    const param = normalizedParams.value[FilterNames.SORT_BY]

    try {
      if (!param) {
        throw new Error('No param')
      }

      const sortParam = Array.isArray(param) ? param[0] : String(param)

      /* bare column names (older URLs stored `sort_by=col` with no
         priority/direction) get the default desc form so the table state
         and the `sort` API param both carry the full col,1,dir format */
      return sortParam.includes(',') ? sortParam : `${sortParam},1,desc`
    } catch {
      return null
    }
  })

  const state = computed<SortingState>({
    get: () => {
      const sort = urlParamToSortState(urlSortState.value)

      return sort ? [sort] : []
    },
    set(sortingState: SortingState) {
      const [first] = sortingState

      const newSortParam = sortStateToURLParam(first)

      if (!newSortParam) {
        deleteUrlParam(FilterNames.SORT_BY)

        return
      }

      if (urlSortState.value !== newSortParam) {
        setUrlParams(FilterNames.SORT_BY, newSortParam)
      }
    },
  })

  return {
    state,
    urlSortState,
  }
}
